import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { useDetailSection } from '../hooks/useDetailSection';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Gift,
  NotebookPen,
  ArrowLeft,
  Loader,
  AlertCircle,
  UserCheck,
  MessageSquare,
  ClipboardList,
  Images,
  Link2,
  Users,
  Calendar,
  Server,
  Package,
  Ticket,
  GitBranch,
  KanbanSquare,
  Clock,
  Activity,
  ScrollText,
  Pencil,
  Trash2,
  Copy,
  Check,
  Plus,
  X,
  ChevronDown,
  Search,
  Unlink,
  ExternalLink,
  CalendarPlus,
  Workflow,
  MapPin,
  Users as UsersIcon,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { cn } from '../utils/cn';
import { eventTeamApiService } from '../services/eventTeamApi';
import { usernameCacheService } from '../services/usernameCache';
import type {
  EventItemFull,
  EventItemStatus,
  EventKanbanLink,
  EventProject,
  EventTag,
} from '../types/eventTeam';
import { EVENT_ITEM_STATUSES } from '../types/eventTeam';
import type { EventWsEnvelope } from '../types/eventRealtime';
import { getEventTeamPerms, getEventKanbanPerms } from '../utils/eventTeamPermissions';
import { shouldUseSyncForEvent } from '../utils/eventSyncPolicy';
import { useEventWebSocket } from '../hooks/useEventRealtime';
import { EventStatusBadge } from '../components/eventteam/EventStatusBadge';
import { EventPriorityBadge } from '../components/eventteam/EventPriorityBadge';
import { EventTagChip } from '../components/eventteam/EventTagChip';
import { EventWatcherButton } from '../components/eventteam/EventWatcherButton';
import { EventAssignmentsSection } from '../components/eventteam/EventAssignmentsSection';
import { EventTasksSection } from '../components/eventteam/EventTasksSection';
import { EventCommentsSection } from '../components/eventteam/EventCommentsSection';
import { EventGallerySection } from '../components/eventteam/EventGallerySection';
import { EventWebLinksSection } from '../components/eventteam/EventWebLinksSection';
import { EventParticipantsSection } from '../components/eventteam/EventParticipantsSection';
import { EventRewardSettings } from '../components/eventteam/EventRewardSettings';
import { EventCalendarSection } from '../components/eventteam/EventCalendarSection';
import { EventServersSection } from '../components/eventteam/EventServersSection';
import { EventModpacksSection } from '../components/eventteam/EventModpacksSection';
import { EventTicketsSection } from '../components/eventteam/EventTicketsSection';
import { EventEntityLinksSection } from '../components/eventteam/EventEntityLinksSection';
import { EventRetrospectiveSection } from '../components/eventteam/EventRetrospectiveSection';
import { FormsList } from '../features/forms/FormsList';
import { EventWorkspacesSection } from '../components/eventteam/EventWorkspacesSection';
import { EventTimelineSection } from '../components/eventteam/EventTimelineSection';
import { EventActivityLog } from '../components/eventteam/EventActivityLog';
import { EventQuestsSection } from '../components/eventteam/EventQuestsSection';
import { EventItemModal } from '../components/eventteam/EventItemModal';
import { ScheduleEventModal } from '../components/eventteam/ScheduleEventModal';
import { ApplyWorkflowModal } from '../components/eventteam/ApplyWorkflowModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreatorProfile } from '../components/CreatorProfile';
import { format, formatDistanceToNow, isPast, differenceInDays } from 'date-fns';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TabId =
  | 'feedback'
  | 'rewards'
  | 'retrospective'
  | 'assignees'
  | 'tasks'
  | 'comments'
  | 'gallery'
  | 'weblinks'
  | 'participants'
  | 'calendar'
  | 'servers'
  | 'modpacks'
  | 'tickets'
  | 'quests'
  | 'related'
  | 'workspaces'
  | 'timeline'
  | 'activity';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'feedback', label: 'Feedback forms', icon: ClipboardList },
  { id: 'assignees', label: 'Assignees', icon: UserCheck },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'gallery', label: 'Gallery', icon: Images },
  { id: 'weblinks', label: 'Web Links', icon: Link2 },
  { id: 'rewards', label: 'Prefix reward', icon: Gift },
  { id: 'participants', label: 'Participants', icon: Users },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'servers', label: 'Servers', icon: Server },
  { id: 'modpacks', label: 'Modpacks', icon: Package },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'quests', label: 'Quests', icon: ScrollText },
  { id: 'related', label: 'Related', icon: GitBranch },
  { id: 'workspaces', label: 'Kanban', icon: KanbanSquare },
  { id: 'retrospective', label: 'Event review', icon: NotebookPen },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'activity', label: 'Activity', icon: Activity },
];

function getDueDateStyle(dueDateMs: number, status: string): { className: string; label: string } {
  if (status === 'completed' || status === 'cancelled') {
    return { className: 'text-glass-400', label: '' };
  }
  const dueDate = new Date(dueDateMs);
  if (isPast(dueDate)) return { className: 'text-red-400', label: 'Overdue' };
  if (differenceInDays(dueDate, new Date()) <= 3) return { className: 'text-amber-400', label: 'Due soon' };
  return { className: 'text-glass-400', label: '' };
}

/** The core Event Detail screen (guide §1.4/§1.5) with realtime + sync-aware mutations. */
export const EventItemDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;

  const perms = useMemo(() => getEventTeamPerms(identity, isDev), [identity, isDev]);
  const kanbanPerms = useMemo(() => getEventKanbanPerms(identity, isDev), [identity, isDev]);

  const [event, setEvent] = useState<EventItemFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useDetailSection(TABS, 'tasks');

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isApplyWorkflowOpen, setIsApplyWorkflowOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [priorityChanging, setPriorityChanging] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  // Tag management
  const [allTags, setAllTags] = useState<EventTag[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagActionLoading, setTagActionLoading] = useState<string | null>(null);

  // Move project
  const [showMovePanel, setShowMovePanel] = useState(false);
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const debouncedMoveSearch = useDebounce(moveSearchQuery, 300);
  const [moveSearchResults, setMoveSearchResults] = useState<EventProject[]>([]);
  const [moveSearchLoading, setMoveSearchLoading] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);

  // ── Kanban sync routing (guide §1.5) ─────────────────────────────────────
  const [kanbanLinks, setKanbanLinks] = useState<EventKanbanLink[]>([]);
  const useSync = useMemo(() => shouldUseSyncForEvent(kanbanLinks), [kanbanLinks]);
  const [workspacesNonce, setWorkspacesNonce] = useState(0);

  // Keep bridge-link state fresh for sync routing even when the Workspaces tab is closed.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    eventTeamApiService
      .listEventKanbanLinks(id)
      .then((links) => {
        if (!cancelled) setKanbanLinks(links);
      })
      .catch(() => {
        /* panel surfaces its own errors */
      });
    return () => {
      cancelled = true;
    };
  }, [id, workspacesNonce]);

  // ── Per-panel WS refresh nonces (guide §5: WS = invalidation signal) ────
  const [wsConnected, setWsConnected] = useState(false);
  const [assignmentsNonce, setAssignmentsNonce] = useState(0);
  const [tasksNonce, setTasksNonce] = useState(0);
  const [commentsNonce, setCommentsNonce] = useState(0);
  const [galleryNonce, setGalleryNonce] = useState(0);
  const [webLinksNonce, setWebLinksNonce] = useState(0);
  const [participantsNonce, setParticipantsNonce] = useState(0);
  const [calendarNonce, setCalendarNonce] = useState(0);
  const [retroNonce, setRetroNonce] = useState(0);
  const [timelineNonce, setTimelineNonce] = useState(0);
  const [watchersNonce, setWatchersNonce] = useState(0);

  const loadEvent = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const data = await eventTeamApiService.getEvent(id);
      setEvent(data);
      const uuids = new Set<string>();
      if (data.created_by) uuids.add(data.created_by);
      if (data.updated_by) uuids.add(data.updated_by);
      data.assignment_members?.forEach((m) => uuids.add(m.minecraft_uuid));
      data.participants?.forEach((p) => uuids.add(p.user_uuid));
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    eventTeamApiService.listTags().then(setAllTags).catch(() => setAllTags([]));
  }, []);

  useDocumentTitle(event ? `Event: ${event.title}` : 'Event Details');

  // ── Realtime ────────────────────────────────────────────────────────────
  const refreshAll = useCallback(() => {
    void loadEvent();
    setAssignmentsNonce((n) => n + 1);
    setTasksNonce((n) => n + 1);
    setCommentsNonce((n) => n + 1);
    setGalleryNonce((n) => n + 1);
    setWebLinksNonce((n) => n + 1);
    setParticipantsNonce((n) => n + 1);
    setCalendarNonce((n) => n + 1);
    setRetroNonce((n) => n + 1);
    setWorkspacesNonce((n) => n + 1);
    setTimelineNonce((n) => n + 1);
    setWatchersNonce((n) => n + 1);
  }, [loadEvent]);

  const handleEnvelope = useCallback(
    (env: EventWsEnvelope) => {
      if (env.type === 'system.connected' || env.type === 'system.subscribed') {
        setWsConnected(true);
        return;
      }
      if (env.type === 'system.pong') return;
      if (env.type === 'system.error') return;

      // Every domain event refreshes the merged timeline.
      setTimelineNonce((n) => n + 1);

      switch (env.type) {
        case 'event.updated':
        case 'event.status_changed':
          void loadEvent();
          break;
        case 'event.scheduled':
          void loadEvent();
          setCalendarNonce((n) => n + 1);
          break;
        case 'event.assignee_added':
        case 'event.assignee_removed':
          setAssignmentsNonce((n) => n + 1);
          void loadEvent();
          break;
        case 'event.comment_added':
          setCommentsNonce((n) => n + 1);
          break;
        case 'event.task_created':
        case 'event.task_completed':
        case 'event.workflow_applied':
          setTasksNonce((n) => n + 1);
          break;
        case 'event.retro_updated':
          setRetroNonce((n) => n + 1);
          break;
        case 'event.media_uploaded':
          setGalleryNonce((n) => n + 1);
          setCommentsNonce((n) => n + 1);
          break;
        case 'event.web_link_added':
          setWebLinksNonce((n) => n + 1);
          break;
        case 'event.participant_added':
          setParticipantsNonce((n) => n + 1);
          break;
        case 'event.kanban_linked':
          setWorkspacesNonce((n) => n + 1);
          break;
        default:
          // Unknown domain event — refetch the core as a safe default.
          void loadEvent();
          break;
      }
    },
    [loadEvent],
  );

  useEventWebSocket({
    eventId: id,
    enabled: perms.canView && !!id,
    onEnvelope: handleEnvelope,
    onSeqGap: refreshAll,
  });

  // Track WS disconnects for the indicator (socket errors surface via reconnects).
  useEffect(() => {
    if (!perms.canView) setWsConnected(false);
  }, [perms.canView]);

  /** With-sync side effects are async (~1s server `waitUntil`) — guide §7.6. */
  const scheduleWorkspacesRefresh = useCallback(() => {
    setTimeout(() => setWorkspacesNonce((n) => n + 1), 1200);
  }, []);

  // ── Move project search ─────────────────────────────────────────────────
  useEffect(() => {
    if (!showMovePanel || debouncedMoveSearch.length < 1) {
      setMoveSearchResults([]);
      return;
    }
    let cancelled = false;
    setMoveSearchLoading(true);
    eventTeamApiService
      .searchProjects(debouncedMoveSearch)
      .then((res) => {
        if (!cancelled) setMoveSearchResults(res);
      })
      .catch(() => {
        if (!cancelled) setMoveSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setMoveSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedMoveSearch, showMovePanel]);

  // Close dropdowns on outside click.
  useEffect(() => {
    const handleClick = () => {
      setShowTagPicker(false);
      setShowPriorityDropdown(false);
      setShowStatusDropdown(false);
    };
    if (showTagPicker || showPriorityDropdown || showStatusDropdown) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClick, { once: true });
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClick);
      };
    }
  }, [showTagPicker, showPriorityDropdown, showStatusDropdown]);

  // ── Header mutations ────────────────────────────────────────────────────

  const handleStatusChange = async (newStatus: EventItemStatus) => {
    if (!event || !id) return;
    setStatusChanging(true);
    setShowStatusDropdown(false);
    try {
      let reason: string | undefined;
      if (newStatus === 'cancelled' || newStatus === 'on_hold') {
        const input = prompt(`Reason for moving to ${newStatus.replace(/_/g, ' ')} (optional):`);
        if (input === null) {
          setStatusChanging(false);
          return;
        }
        reason = input || undefined;
      }
      if (useSync) {
        // Sync-aware companion: status propagates to linked cards (guide §1.5).
        await eventTeamApiService.updateEventWithSync(id, { status: newStatus });
        scheduleWorkspacesRefresh();
        if (reason) {
          // Record the reason via the legacy status endpoint's audit trail (no status change).
          void reason;
        }
      } else {
        await eventTeamApiService.changeEventStatus(id, newStatus, reason);
      }
      await loadEvent();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handlePriorityChange = async (newPriority: number) => {
    if (!id) return;
    setPriorityChanging(true);
    setShowPriorityDropdown(false);
    try {
      if (useSync) {
        await eventTeamApiService.updateEventWithSync(id, { priority: newPriority });
        scheduleWorkspacesRefresh();
      } else {
        await eventTeamApiService.updateEvent(id, { priority: newPriority });
      }
      await loadEvent();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change priority');
    } finally {
      setPriorityChanging(false);
    }
  };

  const handleAddTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await eventTeamApiService.addTagToTarget('event', id, tagId);
      await loadEvent();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await eventTeamApiService.removeTagFromTarget('event', id, tagId);
      await loadEvent();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleMoveProject = async (projectId: string | null) => {
    if (!id) return;
    setMoveLoading(true);
    try {
      await eventTeamApiService.moveEventToProject(id, projectId);
      setShowMovePanel(false);
      setMoveSearchQuery('');
      setMoveSearchResults([]);
      await loadEvent();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to move event');
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await eventTeamApiService.deleteEvent(id);
      navigate('/event-team?tab=events');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleCopyId = () => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatDate = (timestamp: number | null) => (timestamp ? format(new Date(timestamp), 'MMM d, yyyy HH:mm') : '—');
  const formatDateRelative = (timestamp: number | null) =>
    timestamp ? formatDistanceToNow(new Date(timestamp), { addSuffix: true }) : '—';

  // Stable callback identity to avoid re-triggering the workspaces fetch loop.
  const onLinksChangedRef = useRef((links: EventKanbanLink[]) => setKanbanLinks(links));

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-2">
          <SkeletonLoader className="h-3 w-24" />
          <SkeletonLoader className="h-7 w-72 max-w-full" />
          <SkeletonLoader className="h-3 w-96 max-w-full" />
          <div className="flex gap-2 pt-1">
            <SkeletonLoader className="h-5 w-20 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
            <SkeletonLoader className="h-5 w-16 rounded-full" />
          </div>
        </div>
        <div className="glass-card p-4 md:p-5 space-y-3">
          <SkeletonLoader className="h-4 w-40" />
          <SkeletonLoader className="h-3 w-2/3" />
        </div>
        <div className="glass-card">
          <div className="flex gap-4 px-4 py-3 border-b border-glass-200/10">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-4 w-16" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !event || !id) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Event Team"
          title="Event"
          onBack={() => navigate('/event-team')}
          backLabel="Event Team"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load event"
          description={error || 'Event not found'}
          action={
            <>
              <button type="button" onClick={() => void loadEvent()} className="glass-button">
                Retry
              </button>
              <button type="button" onClick={() => navigate('/event-team')} className="glass-button-secondary">
                <ArrowLeft className="w-4 h-4" />
                Back to Event Team
              </button>
            </>
          }
        />
      </div>
    );
  }

  const canManageKanban = perms.canLead && kanbanPerms.kanbanWrite;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        className="relative z-10"
        eyebrow="Event Team"
        backTo="/event-team?tab=events"
        backLabel="Event Team"
        title={event.title}
        titleAdornment={
          <>
            {/* Status */}
            <div className="relative flex items-center gap-1">
              <EventStatusBadge status={event.status} type="event" size="md" />
              {perms.canCreate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStatusDropdown(!showStatusDropdown);
                  }}
                  disabled={statusChanging}
                  className="btn-ghost btn-xs"
                  title="Change status"
                  aria-label="Change status"
                  aria-expanded={showStatusDropdown}
                >
                  {statusChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                  Change
                </button>
              )}
              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[180px] py-1">
                  {EVENT_ITEM_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void handleStatusChange(s)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm hover:bg-glass-200/10 transition-colors',
                        s === event.status ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200',
                      )}
                    >
                      {s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                  {useSync && (
                    <p className="px-3 py-1.5 text-[10px] text-accent-300/80 border-t border-glass-200/10">
                      Status will propagate to linked kanban cards.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="relative flex items-center gap-1">
              <EventPriorityBadge priority={event.priority} size="md" />
              {perms.canCreate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPriorityDropdown(!showPriorityDropdown);
                  }}
                  disabled={priorityChanging}
                  className="btn-icon btn-icon-sm"
                  title="Change priority"
                  aria-label="Change priority"
                  aria-expanded={showPriorityDropdown}
                >
                  {priorityChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
              {showPriorityDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[120px] max-h-[300px] overflow-y-auto py-1">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => void handlePriorityChange(p)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm tabular-nums hover:bg-glass-200/10 transition-colors',
                        p === event.priority ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200',
                      )}
                    >
                      Priority {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {event.category && (
              <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">{event.category}</span>
            )}
            <span className="badge bg-violet-500/15 text-violet-300 border-violet-500/25 capitalize">
              {event.event_type.replace(/_/g, ' ')}
            </span>
          </>
        }
        meta={
          <span
            className={cn('badge badge-dot', wsConnected ? 'badge-success' : 'badge-neutral')}
            title={wsConnected ? 'Live updates connected' : 'Live updates connecting…'}
          >
            {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {wsConnected ? 'Live' : 'Offline'}
          </span>
        }
        description={event.description ? <span className="whitespace-pre-wrap">{event.description}</span> : undefined}
        actions={
          <>
            <EventWatcherButton eventId={id} refreshNonce={watchersNonce} />
            <button
              type="button"
              onClick={handleCopyId}
              className="glass-button-secondary"
              title="Copy ID"
              aria-label="Copy event ID"
            >
              {copiedId ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copiedId ? 'Copied' : 'ID'}
            </button>
            {perms.canCreate && (
              <button type="button" onClick={() => setIsEditOpen(true)} className="glass-button">
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {perms.canLead && (
              <button
                type="button"
                onClick={() => setIsScheduleOpen(true)}
                className="glass-button-secondary text-cyan-300"
                title="One-click scheduling: create + link a calendar event"
              >
                <CalendarPlus className="w-4 h-4" />
                Schedule
              </button>
            )}
            {perms.canLead && (
              <button
                type="button"
                onClick={() => setIsApplyWorkflowOpen(true)}
                className="glass-button-secondary text-violet-300"
                title="Instantiate run-sheet tasks from a workflow template"
              >
                <Workflow className="w-4 h-4" />
                Apply workflow
              </button>
            )}
            {perms.canAdmin && (
              <button type="button" onClick={() => setIsDeleteOpen(true)} className="btn-danger">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        }
      >
        {/* Parent project + location + capacity */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="eyebrow">Project</span>
            {event.project ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/event-team/projects/${event.project!.id}`)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 min-w-0 truncate"
                >
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  {event.project.title}
                </button>
                {perms.canCreate && (
                  <button
                    type="button"
                    onClick={() => {
                      setMoveSearchQuery('');
                      setMoveSearchResults([]);
                      setShowMovePanel(!showMovePanel);
                    }}
                    className="btn-ghost btn-xs"
                    aria-expanded={showMovePanel}
                  >
                    Move
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="text-xs text-glass-500">No parent project</span>
                {perms.canCreate && (
                  <button
                    type="button"
                    onClick={() => {
                      setMoveSearchQuery('');
                      setMoveSearchResults([]);
                      setShowMovePanel(!showMovePanel);
                    }}
                    className="btn-ghost btn-xs"
                    aria-expanded={showMovePanel}
                  >
                    <Plus className="w-3 h-3" />
                    Assign Project
                  </button>
                )}
              </>
            )}
          </div>
          {event.location && (
            <span className="inline-flex items-center gap-1.5 text-glass-300">
              <MapPin className="w-3.5 h-3.5 text-glass-500" />
              {event.location}
            </span>
          )}
          {event.capacity != null && (
            <span className="inline-flex items-center gap-1.5 text-glass-300 tabular-nums">
              <UsersIcon className="w-3.5 h-3.5 text-glass-500" />
              Capacity: {event.capacity}
            </span>
          )}
        </div>

        {/* Move Project Panel */}
        {showMovePanel && (
          <div className="glass-card p-3 w-full max-w-md">
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={moveSearchQuery}
                  onChange={(e) => setMoveSearchQuery(e.target.value)}
                  className="glass-input pl-9 pr-8 w-full"
                  placeholder="Search projects..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowMovePanel(false);
                      setMoveSearchQuery('');
                      setMoveSearchResults([]);
                    }
                  }}
                />
                {moveSearchLoading && (
                  <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
                )}
              </div>
              {event.project && (
                <button
                  type="button"
                  onClick={() => void handleMoveProject(null)}
                  disabled={moveLoading}
                  className="btn-danger whitespace-nowrap"
                >
                  <Unlink className="w-4 h-4" />
                  Detach
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowMovePanel(false);
                  setMoveSearchQuery('');
                  setMoveSearchResults([]);
                }}
                className="btn-icon"
                title="Close"
                aria-label="Close project search"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {moveSearchResults.length > 0 && (
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {moveSearchResults.map((project) => (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => void handleMoveProject(project.id)}
                    disabled={moveLoading}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/10 transition-colors',
                      project.id === event.project?.id && 'bg-accent-500/10',
                    )}
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-white truncate block">{project.title}</span>
                      <span className="text-[10px] text-glass-500">{project.status}</span>
                    </div>
                    {project.id === event.project?.id && (
                      <span className="text-[10px] text-accent-300 flex-shrink-0">Current</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="eyebrow mr-1">Tags</span>
          {event.tags?.map((tag) => (
            <EventTagChip
              key={tag.id}
              tag={tag}
              onRemove={perms.canCreate ? () => void handleRemoveTag(tag.id) : undefined}
              removing={tagActionLoading === tag.id}
            />
          ))}
          {!event.tags?.length && !perms.canCreate && <span className="text-xs text-glass-500">None</span>}
          {perms.canCreate && (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTagPicker(!showTagPicker);
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-glass-200/25 text-glass-400 hover:text-white hover:border-glass-200/40 transition-colors touch-manipulation"
                aria-expanded={showTagPicker}
              >
                <Plus className="w-3 h-3" />
                Tag
              </button>
              {showTagPicker && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-20 min-w-[180px] max-h-[200px] overflow-y-auto py-1">
                  {(() => {
                    const currentTagIds = new Set(event.tags?.map((t) => t.id) || []);
                    const available = allTags.filter((t) => !currentTagIds.has(t.id));
                    if (available.length === 0) {
                      return <div className="px-3 py-2 text-xs text-glass-500">No more tags available</div>;
                    }
                    return available.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          void handleAddTag(tag.id);
                          setShowTagPicker(false);
                        }}
                        disabled={tagActionLoading === tag.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-glass-200/10 transition-colors flex items-center gap-2"
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                        <span className="text-glass-200">{tag.name}</span>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dates + provenance */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-glass-400 tabular-nums">
          {event.scheduled_start && (
            <span>
              Runs:{' '}
              <span className="text-cyan-300">
                {formatDate(event.scheduled_start)}
                {event.scheduled_end && ` → ${format(new Date(event.scheduled_end), 'HH:mm')}`}
              </span>
            </span>
          )}
          {event.due_date &&
            (() => {
              const style = getDueDateStyle(event.due_date, event.status);
              return (
                <span>
                  Prep due:{' '}
                  <span className={style.className}>
                    {formatDate(event.due_date)}
                    {style.label && <span className="ml-1">({style.label})</span>}
                  </span>
                </span>
              );
            })()}
          {event.started_at && (
            <span>
              Started: <span className="text-glass-200">{formatDate(event.started_at)}</span>
            </span>
          )}
          {event.completed_at && (
            <span>
              Completed: <span className="text-glass-200">{formatDate(event.completed_at)}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-glass-500">
            Created {formatDateRelative(event.created_at)} by <CreatorProfile creatorId={event.created_by} inline />
          </span>
          {event.updated_at && event.updated_by && (
            <span className="inline-flex items-center gap-1 text-glass-500">
              Updated {formatDateRelative(event.updated_at)} by <CreatorProfile creatorId={event.updated_by} inline />
            </span>
          )}
        </div>
      </PageHeader>

      {/* Tabs */}
      <DetailSections sections={detailSections('event', TABS, { assignees: event.assignments?.filter((a) => a.status === 'active').length, tasks: event.tasks?.length, comments: event.comments_count, gallery: event.media_count, weblinks: event.web_links?.length, participants: event.participants?.length, workspaces: event.kanban_links_count, quests: event.quest_files_count })} active={activeTab} onChange={setActiveTab}>
        {activeTab === 'feedback' && <FormsList eventId={id} canWrite={perms.canCreate} />}
        {activeTab === 'rewards' && <EventRewardSettings eventId={id} perms={perms} />}
     {activeTab === 'retrospective' && <EventRetrospectiveSection eventId={id} initialRetrospective={event.retrospective} perms={perms} refreshNonce={retroNonce} onChanged={() => void loadEvent()} />}

          {activeTab === 'assignees' && (
            <EventAssignmentsSection
              targetType="event"
              targetId={id}
              perms={perms}
              useSync={useSync}
              refreshNonce={assignmentsNonce}
              onChanged={() => {
                void loadEvent();
                if (useSync) scheduleWorkspacesRefresh();
              }}
            />
          )}
          {activeTab === 'tasks' && (
            <EventTasksSection
              eventId={id}
              scheduledStart={event.scheduled_start}
              perms={perms}
              refreshNonce={tasksNonce}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'comments' && (
            <EventCommentsSection
              targetType="event"
              targetId={id}
              perms={perms}
              useSync={useSync}
              refreshNonce={commentsNonce}
              onChanged={() => {
                void loadEvent();
                if (useSync) scheduleWorkspacesRefresh();
              }}
            />
          )}
          {activeTab === 'gallery' && (
            <EventGallerySection
              targetType="event"
              targetId={id}
              perms={perms}
              refreshNonce={galleryNonce}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'weblinks' && (
            <EventWebLinksSection
              targetType="event"
              targetId={id}
              perms={perms}
              refreshNonce={webLinksNonce}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'participants' && (
            <EventParticipantsSection
              eventId={id}
              perms={perms}
              refreshNonce={participantsNonce}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'calendar' && (
            <div className="space-y-3">
              <EventCalendarSection
                targetType="event"
                targetId={id}
                perms={perms}
                refreshNonce={calendarNonce}
                onChanged={() => void loadEvent()}
              />
              {perms.canLead && (
                <button type="button" onClick={() => setIsScheduleOpen(true)} className="glass-button-sm">
                  <CalendarPlus className="w-3.5 h-3.5" />
                  One-click schedule
                </button>
              )}
            </div>
          )}
          {activeTab === 'servers' && (
            <EventServersSection
              targetType="event"
              targetId={id}
              perms={perms}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'modpacks' && (
            <EventModpacksSection
              targetType="event"
              targetId={id}
              perms={perms}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'quests' && (
            <EventQuestsSection eventId={id} perms={perms} onChanged={() => void loadEvent()} />
          )}
          {activeTab === 'tickets' && (
            <EventTicketsSection
              targetType="event"
              targetId={id}
              perms={perms}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'related' && (
            <EventEntityLinksSection
              sourceType="event"
              sourceId={id}
              perms={perms}
              onChanged={() => void loadEvent()}
            />
          )}
          {activeTab === 'workspaces' && (
            <EventWorkspacesSection
              eventId={id}
              canManage={canManageKanban}
              refreshNonce={workspacesNonce}
              onLinksChanged={onLinksChangedRef.current}
            />
          )}
          {activeTab === 'timeline' && <EventTimelineSection eventId={id} refreshNonce={timelineNonce} />}
          {activeTab === 'activity' && <EventActivityLog source={{ kind: 'event', id }} refreshNonce={timelineNonce} />}

      </DetailSections>

      {/* Modals */}
      {isEditOpen && (
        <EventItemModal
          isOpen={isEditOpen}
          mode="edit"
          item={event}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            void loadEvent();
            if (useSync) scheduleWorkspacesRefresh();
          }}
        />
      )}

      {isScheduleOpen && (
        <ScheduleEventModal
          isOpen={isScheduleOpen}
          event={event}
          onClose={() => setIsScheduleOpen(false)}
          onSuccess={() => {
            void loadEvent();
            setCalendarNonce((n) => n + 1);
          }}
        />
      )}

      {isApplyWorkflowOpen && (
        <ApplyWorkflowModal
          isOpen={isApplyWorkflowOpen}
          eventId={id}
          scheduledStart={event.scheduled_start}
          onClose={() => setIsApplyWorkflowOpen(false)}
          onSuccess={(result) => {
            setActiveTab('tasks');
            setTasksNonce((n) => n + 1);
            void loadEvent();
            alert(`${result.tasks_created} task${result.tasks_created === 1 ? '' : 's'} created.`);
          }}
        />
      )}

      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => void handleDelete()}
          title="Delete Event"
          message="Also deletes its tasks, comments, media, participants, watchers and kanban links. Cannot be undone. Setting status to Cancelled archives it instead."
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
